/**
 * Portfolio list slice (PX-W6.3, TASK-EDD04F2F) — every readable project of
 * the active org, for the `/projects` board.
 *
 * Cache model: the standard `fetchCache` discipline — `condition` skips
 * redundant dispatches (StrictMode double-mount, navigation back), mutations
 * that are not optimistic invalidate, list state initialises EMPTY.
 *
 * Realtime: the `project.*` bus frames arrive as the `realtime/invalidateProjects`
 * nonce action carrying a {@link ProjectsBusFrame} (one dispatched action,
 * consumed by the realtime slice's nonce AND this slice's merge/drop below —
 * the echo rule in `dispatchActivity` already suppressed the local user's own
 * writes before the map ran). Bus payloads carry the project's id ONLY
 * (TASK-7922504E fix cycle 1): `project.updated` marks the list as changed on
 * the server and `features/projects/projectFrameRefetch.ts` reloads it through
 * REST; archive/delete drop the row by id.
 */

import { createAction, createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { projectService } from '../services/projectService.http'
import type { ProjectSummary, ProjectsBusFrame } from '../types'

/** A failed thunk's payload — the i18n-mappable key plus the raw server message. */
export interface ProjectsError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

export interface ProjectsState extends FetchCache {
  items: ProjectSummary[]
  error: ProjectsError | null
}

const initialState: ProjectsState = {
  items: [],
  error: null,
  ...initialFetchCache
}

/** Map any thrown value to the rejection payload the reducers store. */
const toProjectsError = (error: unknown): ProjectsError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/**
 * Load the portfolio for `orgId`. Exactly once per invalidation (StrictMode
 * and remounts included — see `fetchCache`).
 */
export const fetchProjects = createAsyncThunk<
  ProjectSummary[],
  string,
  { rejectValue: ProjectsError }
>('projects/fetchProjects', async (orgId, { rejectWithValue }) => {
  try {
    return await projectService.list(orgId)
  } catch (error) {
    return rejectWithValue(toProjectsError(error))
  }
}, {
  // StrictMode double-mounts and back-navigation dedupe here — see fetchCache.
  condition: (orgId, { getState }) => {
    const { projects } = getState() as { projects: ProjectsState }
    return shouldFetch(projects, orgId)
  }
})

/** Soft-archive a project; the archived row replaces its list entry. */
export const archiveProject = createAsyncThunk<
  ProjectSummary,
  { orgId: string; projectId: string },
  { rejectValue: ProjectsError }
>('projects/archiveProject', async ({ orgId, projectId }, { rejectWithValue }) => {
  try {
    return await projectService.archive(orgId, projectId)
  } catch (error) {
    return rejectWithValue(toProjectsError(error))
  }
})

// ---------------------------------------------------------------------------
// Pure row mutations — shared by the direct reducers and the bus frame case
// ---------------------------------------------------------------------------

/** Spread-merge one server row over the matching list entry (never bare-replace). */
const mergeRow = (state: ProjectsState, row: ProjectSummary): void => {
  const index = state.items.findIndex((item) => item.id === row.id)
  if (index === -1) {
    state.items.push(row)
  } else {
    state.items[index] = { ...state.items[index], ...row }
  }
}

/** Drop a row by slug (archived / deleted). */
const dropRow = (state: ProjectsState, projectId: string): void => {
  state.items = state.items.filter((item) => item.id !== projectId)
}

// ---------------------------------------------------------------------------
// Bus frame alias — SAME action type as the realtime nonce, payload-carrying
// ---------------------------------------------------------------------------

/**
 * Typed alias of `realtime/invalidateProjects` carrying the bus frame. The
 * realtime slice's own reducer bumps its nonce (payload ignored); this slice
 * and `projectDetailSlice` both `addCase` this creator — that is how one
 * socket event fans out to two reducers without touching the shared map's
 * single-action contract.
 */
export const projectsBusFrame = createAction<ProjectsBusFrame>('realtime/invalidateProjects')

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    /**
     * Direct-action seam for a full server row — spread-merge by id. The BUS
     * path (`project.updated` frames) does NOT dispatch these: its frames are
     * id-only and route through the `projectsBusFrame` case below (stale-mark
     * + REST refetch). These actions exist for direct, deterministic tests of
     * the merge/drop semantics.
     */
    projectUpdated(state, action: PayloadAction<ProjectSummary>) {
      mergeRow(state, action.payload)
    },
    /** Direct-action seam: drop an archived engagement from the board. */
    projectArchived(state, action: PayloadAction<ProjectSummary>) {
      dropRow(state, action.payload.id)
    },
    /** Direct-action seam: drop by slug (the ids-only `project.deleted` envelope). */
    projectDeleted(state, action: PayloadAction<{ projectId: string }>) {
      dropRow(state, action.payload.projectId)
    },
    /** Dismiss the error and re-arm the cache (the Retry path). */
    clearProjectsError(state) {
      state.error = null
      invalidate(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        fetchStarted(state)
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.items = action.payload
        fetchSucceeded(state, action.meta.arg)
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        fetchFailed(state)
        state.error = action.payload ?? null
      })
      .addCase(archiveProject.fulfilled, (state, action) => {
        mergeRow(state, action.payload)
      })
      .addCase(projectsBusFrame, (state, action) => {
        const frame = action.payload
        if (frame.event === 'project.updated' && frame.project) {
          // id-only frame: never merge (it would push an empty shell row) —
          // the server truth changed, the refetch listener reloads it.
          markServerChanged(state)
        } else if (frame.event === 'project.archived' && frame.project) {
          dropRow(state, frame.project.id)
        } else if (frame.event === 'project.deleted' && frame.projectId) {
          dropRow(state, frame.projectId)
        }
        // A frame with no resolvable row (or an unknown shape) still bumped the
        // realtime nonce; this slice keeps its cached rows.
      })
  }
})

export const { projectUpdated, projectArchived, projectDeleted, clearProjectsError } =
  projectsSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithProjects {
  projects: ProjectsState
}

export const selectProjectsItems = (s: WithProjects) => s.projects.items
export const selectProjectsLoading = (s: WithProjects) => s.projects.loading
export const selectProjectsError = (s: WithProjects) => s.projects.error
/**
 * The slice's cache fields, for a caller that must know whether the list has
 * been LOADED at all — not merely whether a request is in flight. `loading` is
 * still false on the very first render, before the thunk's `pending` commits.
 */
export const selectProjectsCache = (s: WithProjects): FetchCache => ({
  loading: s.projects.loading,
  invalidated: s.projects.invalidated,
  loadedKey: s.projects.loadedKey,
  loadingKey: s.projects.loadingKey,
  keyScoped: s.projects.keyScoped,
  changedWhileLoading: s.projects.changedWhileLoading
})
